import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { utils } from '@atlaskit/util-service-support';
import { CancellablePromise } from '@atlassian/search-client';
import { SpaceContainerType } from '../../common/clients/common-types';
import { timed } from '../../common/clients';
import { SimpleCache } from '../../utils/simple-cache';
import { ConfluenceObjectResult, ConfluenceSpace, GenericContainerResult, RecentConfluence } from './response-types';
export const RECENT_PAGES_PATH = 'rest/recentlyviewed/1.0/recent';
export const RECENT_SPACE_PATH = 'rest/recentlyviewed/1.0/recent/spaces';
const RECENT_PAGES_LIMIT = 200;
const RECENT_SPACE_LIMIT = 5;
export class ConfluenceRecentsClient {
  constructor({
    url,
    isCollaborationGraphEnabled,
    recentItemsSupplier,
    useRecentWork,
    recentWorkAPIUrl
  }, collabGraphClient) {
    _defineProperty(this, "recentItemsSupplier", async () => {
      if (this.useRecentWork) {
        const {
          createActivityClient
        } = await import( /* webpackChunkName: "@atlaskit-internal_product-search-dialog/recent-work-client" */
        '@atlassian/recent-work-client');
        const activityClient = createActivityClient('v3', 'confluence', undefined, this.recentWorkAPIUrl);
        const {
          viewed
        } = await activityClient.fetchActivities([250], {
          products: ['confluence']
        }, ['viewed']);
        const items = viewed.filter(item => item.object.product === 'confluence').map(item => recentWorkItemToResult(item));
        return {
          items,
          totalSize: items.length,
          timings: 0
        };
      } else {
        const baseUrl = this.serviceConfig.url;
        const recentPagesPromiseWithTimings = this.createRecentRequestPromise(RECENT_PAGES_PATH, {
          limit: RECENT_PAGES_LIMIT
        });
        const {
          result,
          durationMs
        } = await recentPagesPromiseWithTimings;
        const items = result.filter(page => !!page.title).map(recentPage => recentPageToResult(recentPage, baseUrl));
        return {
          items,
          totalSize: items.length,
          timings: durationMs
        };
      }
    });
    _defineProperty(this, "recentSpacesSupplier", () => {
      if (this.isCollabGraphEnabled) {
        return this.collaborationGraphClient.getContainers([SpaceContainerType]).then(response => mapCollabGraphContainer(response));
      } else {
        const recentSpacesPromise = this.createRecentRequestPromise(RECENT_SPACE_PATH, {
          limit: RECENT_SPACE_LIMIT
        });
        return recentSpacesPromise.then(({
          result,
          durationMs
        }) => ({
          items: result.map(recentSpace => recentSpaceToResult(recentSpace, `${this.baseUrl}/spaces/${recentSpace.key}`)),
          timings: durationMs
        }));
      }
    });
    this.serviceConfig = {
      url: url
    };
    this.baseUrl = url;
    this.recentItemCache = new SimpleCache(recentItemsSupplier || this.recentItemsSupplier);
    this.recentSpaceCache = new SimpleCache(this.recentSpacesSupplier);
    this.collaborationGraphClient = collabGraphClient;
    this.isCollabGraphEnabled = isCollaborationGraphEnabled;
    this.useRecentWork = useRecentWork || false;
    this.recentWorkAPIUrl = recentWorkAPIUrl;
  }
  getRecent(cache) {
    const {
      fromCache,
      value
    } = cache.get();
    return CancellablePromise.from(value.then(results => ({
      ...results,
      isCached: fromCache
    })));
  }
  getRecentItems() {
    return this.getRecent(this.recentItemCache);
  }
  getRecentSpaces() {
    return this.getRecent(this.recentSpaceCache);
  }
  createRecentRequestPromise(path, queryParams) {
    const options = {
      path: path,
      queryParams: queryParams
    };
    const requestPromise = utils.requestService(this.serviceConfig, options);
    return timed(requestPromise);
  }
}
function recentWorkItemToResult(recentItem) {
  const {
    object,
    containers
  } = recentItem;
  return {
    resultId: object.id,
    containerName: containers[0].name,
    containerId: '',
    resultType: ConfluenceObjectResult,
    isRecentResult: true,
    name: object.name || '',
    // Failsafe. All items should have a name.
    href: object.url,
    contentType: `confluence-${object.type}`,
    lastModified: undefined,
    // not available
    analyticsType: RecentConfluence
  };
}
function recentPageToResult(recentPage, baseUrl) {
  var _recentPage$spaceId;
  return {
    resultId: String(recentPage.id),
    name: recentPage.title || '',
    // This is a failsafe, there should be a filter to drop pages with no titles
    href: `${baseUrl}${recentPage.url}`,
    containerName: recentPage.space,
    analyticsType: RecentConfluence,
    resultType: ConfluenceObjectResult,
    contentType: `confluence-${recentPage.contentType}`,
    iconClass: recentPage.iconClass,
    containerId: recentPage.spaceKey,
    isRecentResult: true,
    lastModified: undefined,
    // not available for recent results
    spaceId: (_recentPage$spaceId = recentPage.spaceId) === null || _recentPage$spaceId === void 0 ? void 0 : _recentPage$spaceId.toString()
  };
}
function recentSpaceToResult(recentSpace, href) {
  return {
    resultId: recentSpace.id,
    name: recentSpace.name,
    key: recentSpace.key,
    href,
    avatarUrl: recentSpace.icon,
    analyticsType: RecentConfluence,
    resultType: GenericContainerResult,
    contentType: ConfluenceSpace,
    id: recentSpace.id
  };
}
function mapCollabGraphContainer(response) {
  const items = response.collaborationGraphEntities.map(item => {
    var _item$containerDetail, _item$containerDetail2, _item$containerDetail3, _item$containerDetail4, _item$containerDetail5;
    return {
      id: (_item$containerDetail = item.containerDetails) === null || _item$containerDetail === void 0 ? void 0 : _item$containerDetail.id,
      key: (_item$containerDetail2 = item.containerDetails) === null || _item$containerDetail2 === void 0 ? void 0 : _item$containerDetail2.key,
      icon: (_item$containerDetail3 = item.containerDetails) === null || _item$containerDetail3 === void 0 ? void 0 : _item$containerDetail3.iconUrl,
      name: (_item$containerDetail4 = item.containerDetails) === null || _item$containerDetail4 === void 0 ? void 0 : _item$containerDetail4.name,
      url: (_item$containerDetail5 = item.containerDetails) === null || _item$containerDetail5 === void 0 ? void 0 : _item$containerDetail5.url,
      siteId: item.siteId
    };
  }).filter(space => space.id !== undefined).map(space => recentSpaceToResult(space, space.url));
  return {
    items,
    timings: response.timings
  };
}